import Footer from '@components/organisms/FooterNew'
import Edugram500 from '@components/pages/advantageous/Edugram500'
import Explore from '@components/pages/advantageous/Explore'
import FAQSection from '@components/pages/advantageous/FAQSection'
import PublishCourse from '@components/pages/advantageous/PublishCourse'
import Testimonials from '@components/pages/advantageous/Testimonials'
import AuthorDetailsModal from '@components/pages/become-author/AuthorDetailsModal'
import Header from '@components/pages/home/LandingPage/Sections/Header'
import MustExistSection, {
  Tab,
} from '@components/pages/home/LandingPage/Sections/MustExist'
import SafeContent from '@components/pages/home/LandingPage/Sections/SafeContent'
import TrustedStudents from '@components/pages/home/LandingPage/Sections/TrustedStudents'
import HeroSection from '@components/pages/resources/online-courses/sections/HeroSection'
import About from '@components/pages/resources/online-courses/About'
import { useAuth } from '@contexts/Auth'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import withLenis from '@components/HOCs/LenisWrapper'

type Props = {}

const OnlineCourses = ({}: Props) => {
  const [showBecomeAuthorModal, setShowBecomeAuthorModal] = useState(false)
  const { t: tOnlineCourses, i18n } = useTranslation('resources/onlineCourses')
  const { user, isAuthenticated, setShowAuthModal } = useAuth()
  const freshLoggedIn = useRef(false)
  const router = useRouter()

  useEffect(() => {
    if (user && !user?.isAuthor && freshLoggedIn.current) {
      setShowBecomeAuthorModal(true)
    }

    return () => {
      freshLoggedIn.current = false
    }
  }, [user])

  const handleBecomeCreator = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true)
      freshLoggedIn.current = true
      return
    }
    if (user?.isAuthor) router.push('/author')
    else setShowBecomeAuthorModal(true)
  }

  // Load tab data from i18n JSON (resources/onlineCoursesTabs)
  const { t: tTabs } = useTranslation('resources/onlineCoursesTabs')
  const courseTabs: Tab[] = [
    {
      label: tTabs('0.label'),
      title: tTabs('0.title'),
      description: tTabs('0.description'),
      image: '/static/images/features/drag-drop.jpg',
      alt: tTabs('0.alt'),
    },
    {
      label: tTabs('1.label'),
      title: tTabs('1.title'),
      description: tTabs('1.description'),
      image: '/static/images/features/pricing.jpg',
      alt: tTabs('1.alt'),
    },
    {
      label: tTabs('2.label'),
      title: tTabs('2.title'),
      description: tTabs('2.description'),
      image: '/static/images/features/course-page.jpg',
      alt: tTabs('2.alt'),
    },
    {
      label: tTabs('3.label'),
      title: tTabs('3.title'),
      description: tTabs('3.description'),
      image: '/static/images/features/track-progress.jpg',
      alt: tTabs('3.alt'),
    },
    {
      label: tTabs('4.label'),
      title: tTabs('4.title'),
      description: tTabs('4.description'),
      image: '/static/images/features/free-previews.jpg',
      alt: tTabs('4.alt'),
    },
    {
      label: tTabs('5.label'),
      title: tTabs('5.title'),
      description: tTabs('5.description'),
      image: '/static/images/features/messages-hub.jpg',
      alt: tTabs('5.alt'),
    },
    {
      label: tTabs('6.label'),
      title: tTabs('6.title'),
      description: tTabs('6.description'),
      image: '/static/images/features/withdrawals.jpg',
      alt: tTabs('6.alt'),
    },
  ]
  return (
    <div>
      <Head>
        <title>{tOnlineCourses('seo.title')}</title>
        <meta property="og:locale" content={i18n.language} />
        <meta name="og:title" content={tOnlineCourses('seo.title')} />
        <meta name="description" content={tOnlineCourses('seo.description')} />
        <meta
          name="og:description"
          content={tOnlineCourses('seo.description')}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={'Edugram.io'} />
        <meta
          name="twitter:description"
          content={tOnlineCourses('seo.description')}
        />
        <meta
          property="og:image"
          content="/static/images/og_images/online_courses.png"
        />
        <meta
          property="og:image:secure_url"
          content="/static/images/online_courses.png"
        />
        <meta property="og:type" content="online_course" />
        <meta property="og:site_name" content="Edugram.io" />
        <meta property="og:locale" content={i18n.language} />
        <meta name="twitter:domain" content="www.edugram.io" />
        <meta
          name="keywords"
          content="create and sell online courses, online course platform, sell courses online, course creation software"
        />
      </Head>
      <Header handleBecomeCreator={handleBecomeCreator} />
      <HeroSection handleBecomeCreator={handleBecomeCreator} />
      <About />
      <MustExistSection
        initialTabs={courseTabs}
        title={tTabs(`${courseTabs.length}.sectionTitle`)}
        containerClassname="rounded-t-none"
      />
      <PublishCourse
        handleBecomeCreator={handleBecomeCreator}
        isHomeLanding={true}
      />
      <Edugram500 />
      <SafeContent />
      <TrustedStudents handleBecomeCreator={handleBecomeCreator} />
      <Testimonials />
      <FAQSection />
      <Explore />
      <div className="max-w-desktop mx-auto sm:px-4">
        <Footer />
      </div>
      {showBecomeAuthorModal && (
        <AuthorDetailsModal
          isOpen={showBecomeAuthorModal}
          onClose={(ask?: boolean) => {
            if (ask) {
              if (confirm('Are you sure you want to close?')) {
                setShowBecomeAuthorModal((prev) => !prev)
              }
            } else {
              setShowBecomeAuthorModal((prev) => !prev)
            }
          }}
        />
      )}
    </div>
  )
}

export default withLenis(OnlineCourses)

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'authentication',
        'footer',
        'courses',
        'signUp',
        'home',
        'wishlistDropdown',
        'errors',
        'advantageous',
        'homeLanding/header',
        'homeLanding/features',
        'homeLanding/mustexist',
        'homeLanding/herosection',
        'homeLanding/safecontent',
        'homeLanding/trustedstudents',
        'becomeAuthorDetailsModal',
        'reportProblem',
        'resources/onlineCourses',
        'resources/onlineCoursesTabs',
      ])),
    },
  }
}
